import { EventEmitter } from 'events';
import { observable, observe, freeze, computed, root } from 'rewire-core';
import { Form, validators, validator, field, IEditorField, isRequired, ErrorSeverity, IFormContext } from 'rewire-ui';
import { isNullOrUndefined, UTC, utc } from 'rewire-common';
import { reasonCategoryDataMapper, timecardTemplateToTimecard } from '../../utility/DataConverters';
import { createNumberMask, createAccountCodeMask } from '../../utility/Formatters';
import {
  TimecardEntries,
  TimecardEntry,
  TimecardEntriesBase,
  ShiftEntries,
  EmployeeShiftEntries,
  TimecardEntryBase,
} from './TimecardEntries';
import { createUdfSearch, getNameSearcher } from '../../utility/Searchers';
import addReasonDialogModel from '../../schedule/AddReasonDialogModel';
import {
  ITimecard,
  ITemplate,
  ReasonCategory,
  IUdf,
  MultipleType,
  ITimecardTemplate,
  IJobSearchResult,
  INameEntity,
  UdfDocumentType,
} from '../../DataTypes';
import { notifications } from '../../ui/Notifications';
import UdfsModel from '../../schedule/UdfsModel';
import udfsSubscriptionService from '../../services/UdfsSubscriptionService';
import DataQueries from '../../graphql/Queries';
import { renderOccupationSuggestion, renderOccupationSuggestionHeader } from './OccupationSuggestionRenderer';
import { createMultiSelectAutoCompleteEditor } from './MultiSelectAutocompletePopover';
import scheduleModel from '../../schedule/ScheduleModel';

const getKeys = <T extends object>(obj: T) => Object.keys(obj) as Array<keyof T>;
const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Array<[keyof T, T[keyof T]]>;

export interface IDetailFields {
  group?: IEditorField;
  template?: IEditorField;
  start: IEditorField;
  end: IEditorField;
  units: IEditorField;
  reason: IEditorField;
  overtimeReason?: IEditorField;
  employee?: IEditorField;
  tags?: IEditorField;
  occupation?: IEditorField;
  rate?: IEditorField;
  factor?: IEditorField;
  accountCode?: IEditorField;
}

export interface IDetailRow {
  _id: string;
  data: IDetailFields;
  empty: boolean;
  original: TimecardEntry;
  editing: boolean;
  hasChanges: boolean;
}

interface IFieldInfo {
  columns: number;
  position?: number;
  visible: boolean;
  label: string;
  udf: boolean;
}

type IFieldMap = { [key in keyof IDetailFields]?: IFieldInfo };
type IFieldMapVisibility = { [key in keyof IDetailFields]?: boolean };

const reasonSchema = `
id
name
code
display
other(fields: ["category"])
`;

const overtimeReasonFilter = {
  field: 'category',
  in: ['overtime'],
};

export async function getTemplateTimecardTemplates(templateId?: string): Promise<ITimecardTemplate[] | undefined> {
  if (!templateId) {
    return undefined;
  }
  const returnSchema = `
    id
    name
    template {
      start
      end
      units
      factor
      rate
      amount
      accountCode
      reason {
        id
        name
        category
      }
      overtimeReason {
        id
        name
      }
    }
  `;

  const result = await DataQueries.getTemplatesByIds(returnSchema, [templateId]);
  if (result?.[0]?.template) {
    const template = result[0];
    const timecardTemplates = template?.template;
    return timecardTemplates as ITimecardTemplate[];
  }
  return undefined;
}
const tagSearcher = {
  async search(search?: string) {
    return await DataQueries.getTags('rules' as UdfDocumentType, search);
  },
  map(item?: any) {
    return Array.isArray(item) ? '' : item || '';
  },
};

function multiSearchModifer(useSelected: string, searcher: ISearch): ISearch {
  return {
    map(item: any): any {
      return searcher.map(item);
    },
    async search(search?: string | undefined) {
      const results = (await searcher.search(search)) ?? [];
      if (!scheduleModel.isMultiSelect) {
        return results;
      }

      if (search && search.length > 0) {
        return [...results, { id: 'none', name: `«${useSelected}»` }];
      }
      return [{ id: 'none', name: `«${useSelected}»` }, ...results];
    },
  };
}

const groupSearcher = multiSearchModifer('Group', getNameSearcher('groups'));
const templateSearcher = multiSearchModifer('Template', getNameSearcher('templates'));
const overtimeReasonSearcher = multiSearchModifer(
  'Overtime Reason',
  getNameSearcher('reasons', () => overtimeReasonFilter, reasonSchema, reasonCategoryDataMapper),
);
const employeeSearcher = multiSearchModifer('Employee', getNameSearcher('employees'));

const filter = {
  field: 'category',
  in: [],
} as { field: string; in: ReasonCategory[] };
const searcher = multiSearchModifer(
  'Reason',
  getNameSearcher('reasons', () => filter, reasonSchema, reasonCategoryDataMapper),
);

const reasonSearcher = (model: EditTimecardModel) => {
  return {
    map(item: any): any {
      return searcher.map(item);
    },

    search(search?: string) {
      const detailRow = model.currentRow;
      filter.in = [];

      if (!detailRow || detailRow.empty) {
        filter.in.push('worked', 'absence');
        if (model.showBreaks) filter.in.push('break');
        if (model.showPremiums) filter.in.push('premium');
      } else {
        switch (detailRow.data.reason.value?.category) {
          case 'premium':
            filter.in.push('premium');
            break;

          case 'other':
            filter.in.push('other');
            break;

          default:
            filter.in.push('worked', 'absence');
            if (model.showBreaks) filter.in.push('break');
            break;
        }
      }
      return searcher.search(search);
    },
  };
};

function performJobSearch(groupId: string, employeeId: string | undefined, effective: UTC, search: string) {
  return DataQueries.jobSearch(
    `
      id
      isQualified
      occupation { id code name }
      group { id name }
      generator { name }
      employee { name }
    `,
    search,
    groupId,
    employeeId,
    effective,
    25,
  );
}

const occupationSearcher = (model: EditTimecardModel) => {
  return {
    map(item: any): any {
      if (!item) return '';
      if (item.generator?.name) {
        return `${item.name} (${item.generator?.name ?? ''} - ${item.employee?.name ?? ''})`;
      }
      return item?.name ?? '';
    },
    async search(text: string) {
      const results = await performJobSearch(
        model.currentRow?.data.group?.value?.id,
        model.currentRow?.original?.employee?.id,
        model.earnedDate,
        text,
      );
      if (!results) return [];
      for (const item of results as (IJobSearchResult & INameEntity)[]) {
        if (!item.occupation) continue;
        item.name = item.occupation.name;
        item.code = item.occupation.code;
      }
      return results;
    },
  };
};

function getOccupationTooltipFn(detail: TimecardEntry) {
  return (value?: string): string | undefined => {
    if (!value) return undefined;
    const generator = detail?.job?.generator?.name;
    const employee = detail?.job?.employee?.name;
    if (generator) return `${generator} -> ${value} (${employee ?? '«none»'})`;
    return value;
  };
}

function timeMap(value: any) {
  if (value === Number.NEGATIVE_INFINITY) return '';
  if (value === Number.POSITIVE_INFINITY) return '';
  return value;
}

interface IEditTimecardModelState {
  __currentRow: IDetailRow | undefined;
  hasChanges: boolean;
  currentUnits: number;
  expectedUnits: number;
  condensedTimecard: boolean;
  showPremiums: boolean;
  showBreaks: boolean;
  breakdownMode: boolean;
  showUdfs: boolean;
  viewOnlyFn: () => boolean;
}

export interface IEditTimecardModelOptions {
  condensedTimecard?: boolean;
  showPremiums?: boolean;
  showUdfs?: boolean;
  showBreaks?: boolean;
  breakdownMode?: boolean;
  timecardEntries?: TimecardEntriesBase;
  fieldMap?: IFieldMap;
  condensedTimecardFieldMapVisibility?: IFieldMapVisibility;
  viewOnlyFn?: () => boolean;
}

interface ISearch {
  map(item: any): any;
  search(search?: string | undefined): any;
}

export class EditTimecardModel<T = unknown> extends EventEmitter {
  public form: Form;
  public timecardEntries: TimecardEntriesBase;
  public detailRows: IDetailRow[] = observable([]);
  protected state: IEditTimecardModelState & T;
  private _udfEditor!: UdfsModel;
  protected _fieldMap: IFieldMap;
  private _occupationSearcher: ISearch;
  private _reasonSearcher: ISearch;
  protected _standardTimecardFieldMapVisibility: IFieldMapVisibility;
  protected _condensedTimecardFieldMapVisibility: IFieldMapVisibility;
  protected _hasDefaultGroupAndOccupation!: () => boolean;

  constructor(
    private _id: string,
    _options?: IEditTimecardModelOptions,
  ) {
    super();
    const options = Object.assign(
      {},
      _options ?? null,
      JSON.parse(localStorage.getItem(this._id) as string) as IEditTimecardModelOptions,
    );
    this.timecardEntries = options?.timecardEntries ?? new TimecardEntries();
    this._occupationSearcher = occupationSearcher(this);
    this._reasonSearcher = reasonSearcher(this);
    this.state = observable({
      currentUnits: 0,
      hasChanges: false,
      expectedUnits: 0,
      __currentRow: undefined,
      condensedTimecard: options?.condensedTimecard ?? false,
      showBreaks: options?.showBreaks ?? true,
      breakdownMode: options?.breakdownMode ?? false,
      showUdfs: options?.showUdfs ?? true,
      showPremiums: options?.showPremiums ?? false,
      viewOnlyFn: options?.viewOnlyFn ?? (() => false),
    }) as IEditTimecardModelState & T;
    this.initializeTimecardEntries();

    this._fieldMap = options?.fieldMap ?? {
      group: { visible: true, columns: 2, udf: false, label: 'Group' },
      occupation: { visible: true, columns: 2, udf: false, label: 'Occupation' },
      template: { visible: true, columns: 2, udf: false, label: 'Shift Type' },
      start: { visible: true, columns: 1, udf: false, label: 'Start' },
      end: { visible: true, columns: 1, udf: false, label: 'End' },
      employee: { visible: false, columns: 2, udf: false, label: 'Employee' },
      units: { visible: true, columns: 1, udf: false, label: 'Units' },
      reason: { visible: true, columns: 2, udf: false, label: 'Reason' },
      overtimeReason: { visible: true, columns: 2, udf: false, label: 'Overtime Reason' },
      rate: { visible: true, columns: 1, udf: false, label: 'Rate' },
      factor: { visible: true, columns: 1, udf: false, label: 'Factor' },
      accountCode: { visible: true, columns: 2, udf: false, label: 'Account Code' },
      tags: { visible: true, columns: 2, udf: false, label: 'Tags' },
    };

    this._standardTimecardFieldMapVisibility = getKeys(this._fieldMap).reduce(
      (prev: IFieldMapVisibility, curr: keyof IDetailFields) => {
        prev[curr] = this._fieldMap[curr]?.visible ?? false;
        return prev;
      },
      {},
    );

    this._condensedTimecardFieldMapVisibility = options?.condensedTimecardFieldMapVisibility ?? {
      group: false,
      occupation: false,
      template: true,
      start: true,
      end: true,
      employee: false,
      units: true,
      reason: true,
      overtimeReason: false,
      rate: false,
      accountCode: false,
      tags: false,
    };

    this.form = Form.create(
      () => ({}),
      {},
      {
        defaultAdornmentsEnabled: false,
        variant: 'outlined',
        updateOnChange: true,
        useTooltipForErrors: true,
      },
    );

    root(() => {
      const hasDefaultGroupAndOccupationComputation = () => {
        const defaultTimecard = this.timecardEntries?.defaultEmptyTimecard;
        const job = defaultTimecard?.job;
        const group = job?.group ?? defaultTimecard?.group;
        const occupation = job?.occupation ?? defaultTimecard?.occupation;
        return !!(group && occupation);
      };
      const defaultEmptyTimecardChanged = observe(() => {
        this.timecardEntries?.defaultEmptyTimecard;
      });
      this._hasDefaultGroupAndOccupation = computed(defaultEmptyTimecardChanged, hasDefaultGroupAndOccupationComputation, false);
    });
  }

  getFieldInfo(key: keyof IFieldMap): IFieldInfo {
    return this._fieldMap[key]!;
  }

  setVisibility(key: keyof IFieldMap, visible: boolean): void {
    this._standardTimecardFieldMapVisibility[key] = visible;
    this._condensedTimecardFieldMapVisibility[key] = visible;
    this._fieldMap[key]!.visible = visible;
  }

  getInitialFieldVisibility(key: keyof IFieldMapVisibility): boolean {
    return (
      (this.condensedTimecard ? this._condensedTimecardFieldMapVisibility[key] : this._standardTimecardFieldMapVisibility[key]) ??
      false
    );
  }

  get udfs(): IUdf[] {
    return udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('timecards');
  }

  get fieldMap() {
    return this._fieldMap;
  }

  get totalCurrentUnits() {
    return this.state.currentUnits;
  }

  get totalExpectedUnits() {
    return this.state.expectedUnits;
  }

  get viewOnly() {
    return this.state.viewOnlyFn();
  }

  initializeTimecardEntries() {
    this.timecardEntries.setShowBreaks(this.state.showBreaks);
    this.timecardEntries.breakdownMode = this.state.breakdownMode;
    this.timecardEntries.showPremiums = this.state.showPremiums;
  }

  remove(detailRow: IDetailRow) {
    this.timecardEntries.remove(detailRow.original);
    this.refresh(true);
    const currentRow = this.currentRow;
    this.state.__currentRow = undefined;
    this.state.hasChanges = true;
    this.emit('edit', currentRow);
  }

  private isUdf(key: keyof IFieldMap): boolean {
    return (this._fieldMap[key] as IFieldInfo).udf;
  }

  createTimecardEntry(row: IDetailRow, empty?: boolean) {
    const values: Record<string, any> = Object.assign({}, row.original);
    if (row.original.fields) {
      values.fields = Object.assign({}, row.original.fields);
    }
    for (const [key, field] of getEntries(row.data)) {
      const value = field?.value;
      if (this.isUdf(key)) {
        if (value !== undefined) (values['fields'] || (values['fields'] = {}))[key] = value;
        else delete values?.fields?.[key];
      } else {
        if (value !== undefined) values[key] = value;
        else delete values[key];
      }
    }
    if (
      isNullOrUndefined(values.start) ||
      isNullOrUndefined(values.end) ||
      (!empty && (values.start === Number.NEGATIVE_INFINITY || values.end === Number.POSITIVE_INFINITY))
    ) {
      return TimecardEntryBase.create(
        row._id,
        values.start,
        values.end,
        values.units,
        {
          accountCode: values.accountCode,
          job: row.original.job,
          shift: row.original.shift,
          employee: values.employee,
          units: values.units,
          occupation: values.occupation,
          reason: values.reason,
          overtimeReason: values.overtimeReason,
          group: values.group,
          template: values.template,
          tags: values.tags,
          rate: values.rate,
          factor: values.factor,
          amount: row.original.amount,
          fields: values.fields,
        },
        empty ?? row.original.isEmpty,
      );
    }

    return TimecardEntry.create(
      row._id,
      values.start,
      values.end,
      values.units,
      {
        accountCode: values.accountCode,
        job: row.original.job,
        shift: row.original.shift,
        employee: values.employee,
        occupation: values.occupation,
        reason: values.reason,
        overtimeReason: values.overtimeReason,
        group: values.group,
        template: values.template,
        tags: values.tags,
        rate: values.rate,
        factor: values.factor,
        amount: row.original.amount,
        fields: values.fields,
      },
      empty ?? row.original.isEmpty,
    );
  }

  deleteTimecard = async () => {
    if (!this.timecardEntries.defaultEmptyTimecard) {
      return;
    }
    this.timecardEntries.clear();
    await this.refresh(true);
    const currentRow = this.detailRows.find((row) => row._id === this.currentRow?._id);
    if (currentRow) {
      currentRow.hasChanges = false;
      currentRow.editing = true;
    }
    this.state.__currentRow = currentRow;
    this.emit('edit', currentRow);
  };

  get earnedDate() {
    return this.timecardEntries.earnedDate;
  }

  undoRow() {
    if (this.viewOnly || !this.currentRow || !this.currentRow.hasChanges) return;
    this.currentRow.hasChanges = false;
    this.form.revert();
  }

  async edit(): Promise<boolean> {
    if (this.viewOnly || !this.currentRow || !this.currentRow.hasChanges) return true;
    try {
      const edit = this.createTimecardEntry(this.currentRow, false);
      this.timecardEntries.edit(edit);
      await this.refresh(true);
      const currentRow = this.detailRows.find((row) => row._id === this.currentRow?._id);
      if (currentRow) {
        currentRow.hasChanges = false;
        currentRow.editing = true;
      }
      this.state.__currentRow = currentRow;
      this.emit('edit', currentRow);
      return true;
    } catch (err) {
      notifications.error(err);
      return false;
    }
  }

  private checkForChanges() {
    const row = this.state.__currentRow;
    if (!row) return;
    const current = this.createTimecardEntry(row, row.empty);
    const changes = !this.timecardEntries.equals(current, row.original);
    if (changes !== row.hasChanges) {
      row.hasChanges = changes;
    }
  }

  get currentRow() {
    return this.state.__currentRow;
  }
  set currentRow(newRow) {
    const row = this.state.__currentRow;
    if (row) {
      if (row.hasChanges || (newRow && row._id === newRow._id)) return;
    }
    freeze(() => {
      if (row) {
        row.hasChanges = false;
        row.editing = false;
      }
      this.state.__currentRow = newRow;
      if (newRow) newRow.editing = true;
    });
  }

  reset() {
    this.form.reset();
    this.state.__currentRow = undefined;
  }

  get condensedTimecard() {
    return this._hasDefaultGroupAndOccupation() ? this.state.condensedTimecard : false;
  }
  set condensedTimecard(value: boolean) {
    if (this.state.condensedTimecard === value || !this.canSetCondensedTimecard()) return;
    this.state.condensedTimecard = value;
    this.refresh(this.state.hasChanges);
  }
  canSetCondensedTimecard() {
    return !this.currentRow?.hasChanges && this._hasDefaultGroupAndOccupation();
  }

  get showPremiums() {
    return this.state.showPremiums;
  }
  set showPremiums(value: boolean) {
    if (this.state.showPremiums === value || !this.canSetShowPremiums()) return;
    this.timecardEntries.showPremiums = this.state.showPremiums = value;
    this.refresh(this.state.hasChanges);
  }
  canSetShowPremiums() {
    return !this.currentRow?.hasChanges;
  }

  get breakdownMode() {
    return this.state.breakdownMode;
  }
  set breakdownMode(value: boolean) {
    if (this.state.breakdownMode === value || !this.canSetBreakdownMode()) return;
    this.timecardEntries.breakdownMode = this.state.breakdownMode = value;
    this.refresh(this.state.hasChanges);
  }
  canSetBreakdownMode() {
    return !this.currentRow?.hasChanges;
  }

  get showBreaks() {
    return this.state.showBreaks;
  }
  set showBreaks(value: boolean) {
    if (this.state.showBreaks === value || !this.canSetShowBreaks()) return;
    const hasChanges = this.timecardEntries.setShowBreaks((this.state.showBreaks = value)) || this.state.hasChanges;
    this.refresh(hasChanges);
  }
  canSetShowBreaks() {
    return !this.currentRow?.hasChanges;
  }

  get showUdfs() {
    return this.state.showUdfs;
  }
  set showUdfs(value: boolean) {
    if (this.state.showUdfs === value || !this.canSetShowUdfs()) return;
    this.state.showUdfs = value;
    this.refresh(this.state.hasChanges);
  }
  canSetShowUdfs() {
    return !this.currentRow?.hasChanges;
  }

  async refresh(setHasChanges = false) {
    this.form.value = {};
    const currentRow = this.currentRow;

    this.form.removeFields(this.form.fields.map((f) => f.name));
    localStorage.setItem(
      this._id,
      JSON.stringify({
        condensedTimecard: this.state.condensedTimecard,
        breakdownMode: this.state.breakdownMode,
        showBreaks: this.state.showBreaks,
        showPremiums: this.state.showPremiums,
        showUdfs: this.state.showUdfs,
      }),
    );

    const entries = this.timecardEntries.entries.map((entry) => this.addFormTimecardEntryRow(entry));
    this.detailRows.set(entries);

    this.state.currentUnits = this.timecardEntries.totalUnits;
    this.form.value = this.form.toObjectValues(); // Acts as a "commit".
    const newCurrentRow = this.detailRows.find((r) => r._id === currentRow?._id);
    if (currentRow && newCurrentRow && newCurrentRow !== currentRow) {
      this.state.__currentRow = newCurrentRow;
    }
    if (this.state.hasChanges !== setHasChanges) this.state.hasChanges = setHasChanges;
  }

  async load(
    defaultEmptyTimecard: ITimecard,
    expectedTemplate: ITemplate | undefined | MultipleType,
    details: ITimecard[],
    hasChanges = false,
  ) {
    this.timecardEntries.set(defaultEmptyTimecard, details);
    if (expectedTemplate && (expectedTemplate as ITemplate).template) {
      this.state.expectedUnits = (expectedTemplate as ITemplate).template?.reduce((prev, curr) => {
        return prev + (curr.units && ['worked', 'absence'].includes(curr.reason?.category ?? '') ? curr.units : 0);
      }, 0);
    } else {
      this.state.expectedUnits = 0;
    }
    this.state.__currentRow = undefined;
    return this.refresh(hasChanges);
  }

  addFormTimecardEntryRow(detail: TimecardEntry) {
    const detailsRow = this.createFormDetailRow(detail) as IDetailRow;
    this.setDetailRowFieldProperties(detailsRow);
    const values: Record<string, any> = {};
    for (const [key, field] of getEntries(detailsRow.data)) {
      if (!field) continue;
      values[field.name] = this.isUdf(key) ? detail.fields?.[key] : detail[key];
      this.onValueChangeHandler(field);
    }

    this.form.value = Object.assign({}, this.form.value, values);
    return detailsRow;
  }

  get udfEditor() {
    return this._udfEditor || (this._udfEditor = new UdfsModel('timecards'));
  }

  async addUdfs(detailId: string, _: IFormContext, fields: any, disabled: () => boolean) {
    if (!this.udfs?.length) return;
    for (const udf of this.udfs) {
      const name = `${udf.name}-${detailId}`;
      switch (udf.type) {
        case 'text': {
          let field: any;
          if (udf.isList) {
            field = udf.allowableValues
              ? _.multiselectautocomplete(
                  multiSearchModifer(
                    udf.displayName,
                    createUdfSearch(udf, (v) => v.name),
                  ),
                  { chipLimit: 3 },
                )
              : _.string();
          } else {
            field = udf.allowableValues
              ? _.select(
                  multiSearchModifer(
                    udf.displayName,
                    createUdfSearch(udf, (v) => v.name),
                  ),
                ).editor('auto-complete')
              : _.string();
          }
          fields[name] = field.disabled(disabled);
          break;
        }
        case 'date':
          // fields[udf.name] = _.date().accessor(['fields', udf.name]);
          fields[name] = _.date()
            .disabled(disabled)
            .accessor(
              (obj) => obj?.fields?.[udf.name],
              (obj, value) => {
                if (!obj) return;
                if (!obj.fields) obj.fields = {};
                const newValue = value && (value instanceof UTC ? value : utc(value.toString()));
                if (newValue) {
                  obj.fields[name] = newValue;
                } else {
                  delete obj.fields[name];
                }
              },
            );
          break;
        case 'float':
          fields[name] = _.number({ thousandSeparator: true, decimals: 4 }).disabled(disabled);
          break;
        case 'integer':
          fields[name] = _.number({ thousandSeparator: true, decimals: 0 }).disabled(disabled);
          break;
        case 'boolean':
          fields[name] = _.boolean().disabled(disabled);
          break;
        case 'reference': {
          const searcher = multiSearchModifer(udf.displayName, createUdfSearch(udf));
          const searchField = udf.isList
            ? _.custom(createMultiSelectAutoCompleteEditor(), { chipLimit: 3, searcher: searcher, title: udf.displayName })
            : _.select(searcher).editor('auto-complete');
          fields[name] = searchField.disabled(disabled);
          break;
        }
      }
    }
  }

  protected createFormDetailRow(detail: TimecardEntry): IDetailRow {
    const rateMaskFn = createNumberMask({
      prefix: '',
      allowDecimal: true,
      decimalLimit: 4,
      allowNegative: true,
      allowLeadingZeroes: true,
      allowMultiple: true,
    });
    const accountCodeMaskFn = createAccountCodeMask({ allowMultiple: true });

    const disabled = () => !!(this.viewOnly || (this.currentRow?.hasChanges && detailRow._id !== this?.currentRow._id));

    function reasonRequiredValidator(value: any) {
      return !detail.isEmpty && !isRequired(value) ? { text: 'reason is required', severity: ErrorSeverity.Error } : undefined;
    }

    this.form.addFields((_) => {
      const fields: Record<string, any> = {};
      if (this.getInitialFieldVisibility('group')) {
        const groupField = _.reference(groupSearcher, { hasTransition: false }).validators('required').disabled(disabled);
        fields[`group-${detail.id}`] = groupField;
      }
      if (this.getInitialFieldVisibility('template')) {
        const templateField = _.reference(templateSearcher).disabled(disabled);
        fields[`template-${detail.id}`] = templateField;
      }
      if (this.getInitialFieldVisibility('start')) {
        const startField = _.time({ map: timeMap, id: `start-${detail.id}`, rounding: 0 }).disabled(disabled);
        fields[`start-${detail.id}`] = startField;
      }
      if (this.getInitialFieldVisibility('end')) {
        const endField = _.time({ map: timeMap, rounding: 0 }).disabled(disabled);
        fields[`end-${detail.id}`] = endField;
      }
      if (this.getInitialFieldVisibility('employee')) {
        const employeeField = _.reference(employeeSearcher).disabled(disabled);
        fields[`employee-${detail.id}`] = employeeField;
      }
      if (this.getInitialFieldVisibility('units')) {
        const unitsField = _.number({ decimals: 4, thousandSeparator: false, allowNegative: false })
          .disabled(disabled)
          .updateOnChange(false);
        fields[`units-${detail.id}`] = unitsField;
      }
      if (this.getInitialFieldVisibility('reason')) {
        const reasonField = _.reference(this._reasonSearcher, { hasTransition: false })
          .validators(reasonRequiredValidator)
          .disabled(disabled);
        fields[`reason-${detail.id}`] = reasonField;
      }
      if (this.getInitialFieldVisibility('overtimeReason')) {
        const overtimeReasonField = _.reference(overtimeReasonSearcher).disabled(disabled);
        fields[`overtimeReason-${detail.id}`] = overtimeReasonField;
      }
      if (this.getInitialFieldVisibility('occupation')) {
        const occupationField = _.reference(this._occupationSearcher, {
          tooltip: getOccupationTooltipFn(detail),
          hasTransition: false,
          renderSuggestion: renderOccupationSuggestion,
          suggestionsContainerHeader: renderOccupationSuggestionHeader,
        })
          .validators('required')
          .disabled(disabled);
        fields[`occupation-${detail.id}`] = occupationField;
      }
      if (this.getInitialFieldVisibility('rate')) {
        const rateField = _.mask({ mask: rateMaskFn, showMask: false }).disabled(disabled);
        fields[`rate-${detail.id}`] = rateField;
      }
      if (this.getInitialFieldVisibility('factor')) {
        const factorField = _.number().disabled(disabled);
        fields[`factor-${detail.id}`] = factorField;
      }
      if (this.getInitialFieldVisibility('accountCode')) {
        const accountCodeField = _.mask({ mask: accountCodeMaskFn, showMask: false }).disabled(disabled);
        fields[`accountCode-${detail.id}`] = accountCodeField;
      }
      if (this.getInitialFieldVisibility('tags')) {
        const tagsField = _.custom(createMultiSelectAutoCompleteEditor(), {
          chipLimit: 3,
          searcher: tagSearcher,
          title: 'Tag',
        }).disabled(disabled);
        fields[`tags-${detail.id}`] = tagsField;
      }
      if (this.state.showUdfs) {
        // hack for now fix later!!
        this.addUdfs(detail.id, _, fields, disabled);
      }
      return fields;
    }, {});

    const data: Partial<IDetailFields> = {};
    for (const key of getKeys(this._fieldMap)) {
      const fieldInfo = this.getFieldInfo(key);
      if (fieldInfo.udf) delete this._fieldMap[key];
      else {
        if (this.getInitialFieldVisibility(key)) data[key] = this.form.field[`${key}-${detail.id}`];
      }
    }

    if (this.state.showUdfs) {
      for (const udf of this.udfs) {
        const udfName = udf.name as keyof Partial<IDetailFields>;
        const field = this.form.field[`${udf.name}-${detail.id}`];
        data[udfName] = field;
        if (!this._fieldMap[udfName])
          this._fieldMap[udfName] = {
            columns: 3, // udf.type === 'reference' ? 2 : 1,
            visible: true,
            label: udf.displayName,
            udf: true,
          };
      }
    }

    const detailRow: IDetailRow = {
      _id: detail.id,
      editing: false,
      hasChanges: false,
      original: detail,
      empty: detail.isEmpty,
      data: data as IDetailFields,
    };

    return detailRow;
  }

  showAddReasonDialog() {
    addReasonDialogModel.load();
  }

  onValueChangeHandler(field: IEditorField) {
    const oldOnValueChange = field.onValueChange;
    field.onValueChange = (form: Form, value: any) => {
      if (oldOnValueChange) {
        oldOnValueChange(form, value);
      }
      this.checkForChanges();
    };
  }
  setDetailRowFieldProperties(detailRow: IDetailRow) {
    const startField = detailRow.data.start;
    const endField = detailRow.data.end;
    if (isNullOrUndefined(detailRow.original.start)) {
      startField.disabled = () => true;
      endField.disabled = () => true;
    } else {
      const unitsField = detailRow.data.units;
      startField.onValueChange = this.handleOnStartChanged(detailRow, startField, endField, unitsField);
      endField.onValueChange = this.handleOnEndChanged(detailRow, startField, endField, unitsField);
      unitsField.onValueChange = this.handleOnUnitsChanged(detailRow, startField, endField, unitsField);
      this.form.validator.setRule(
        unitsField.name,
        validators(['required', validator('>', 0, field(startField.name), field(endField.name))]),
      );
    }

    const templatesField = detailRow.data.template;
    const occupationsField = detailRow.data.occupation;
    const groupsField = detailRow.data.group;

    if (templatesField) {
      templatesField.onValueChange = this.handleOnTemplateChanged(detailRow, templatesField);
    }
    if (groupsField) {
      groupsField.onValueChange = (_form, _value) => {
        if (detailRow.data.occupation?.value) detailRow.data.occupation.value = undefined;
      };
    }
    if (occupationsField) {
      occupationsField.onValueChange = (form, value) => {
        const currentRow = this.state.__currentRow;
        if (!value || !value.id || !currentRow || !value.occupation) return;
        if (value.id !== currentRow.original.job?.id) {
          const job = {
            id: value.id,
            group: value.group,
            occupation: value.occupation,
            generator: value.generator,
            employee: value.employee,
          } as any;
          currentRow.original.job = job;
          requestAnimationFrame(() => {
            currentRow.hasChanges = true;
            currentRow.editing = true;
          });
        }
        occupationsField.value = value.occupation;
      };
    }
  }

  private getReason(detailRow: IDetailRow) {
    if (!detailRow.data) return undefined;
    return detailRow.data.reason.value ?? detailRow.data.template?.value?.template?.[0].reason;
  }

  handleOnStartChanged = (detailRow: IDetailRow, start: IEditorField, end: IEditorField, units: IEditorField) => () => {
    requestAnimationFrame(() => (this.currentRow = this.state.__currentRow));
    if (isNullOrUndefined(start.value)) {
      if (detailRow.original.start === Number.NEGATIVE_INFINITY) {
        start.value = Number.NEGATIVE_INFINITY;
      }
      return;
    }

    if (start.value === Number.NEGATIVE_INFINITY) {
      return;
    }

    const reason = this.getReason(detailRow);

    if (!isNullOrUndefined(end.value) && end.value !== Number.POSITIVE_INFINITY) {
      units.value = this.timecardEntries.computeUnits(reason, start.value, end.value);
    } else if (!isNullOrUndefined(units.value) && units.value !== Number.POSITIVE_INFINITY) {
      end.value = this.timecardEntries.computeEndValue(reason, start.value, units.value);
    }
  };

  handleOnEndChanged = (detailRow: IDetailRow, start: IEditorField, end: IEditorField, units: IEditorField) => () => {
    if (isNullOrUndefined(end.value)) {
      if (detailRow.original.end === Number.POSITIVE_INFINITY) {
        end.value = Number.POSITIVE_INFINITY;
      }
      return;
    }

    if (end.value === Number.POSITIVE_INFINITY) {
      return;
    }

    const reason = this.getReason(detailRow);
    if (!isNullOrUndefined(start.value) && start.value !== Number.NEGATIVE_INFINITY) {
      units.value = this.timecardEntries.computeUnits(reason, start.value, end.value);
    } else if (!isNullOrUndefined(units.value) && units.value !== Number.POSITIVE_INFINITY) {
      start.value = this.timecardEntries.computeStartValue(reason, end.value, units.value);
    }
  };

  handleOnUnitsChanged = (detailRow: IDetailRow, start: IEditorField, end: IEditorField, units: IEditorField) => () => {
    if (!isNullOrUndefined(units.value) && units.value !== Number.POSITIVE_INFINITY) {
      const reason = this.getReason(detailRow);
      if (!isNullOrUndefined(start.value) && start.value !== Number.NEGATIVE_INFINITY) {
        end.value = this.timecardEntries.computeEndValue(reason, start.value, units.value);
      } else if (!isNullOrUndefined(end.value) && end.value !== Number.POSITIVE_INFINITY) {
        start.value = this.timecardEntries.computeStartValue(reason, end.value, units.value);
      }
    }
  };

  handleOnTemplateChanged = (detailRow: IDetailRow, _templateField: IEditorField) => async (form: Form, value: any) => {
    const original = !detailRow.empty ? detailRow.original : undefined;
    const existingTemplate = original?.template;
    const newTemplate = value;
    if (!newTemplate?.id || newTemplate?.id === existingTemplate?.id) return;
    const timecardTemplates = newTemplate?.template ?? (await getTemplateTimecardTemplates(newTemplate.id));
    if (!timecardTemplates) return;
    newTemplate.template = timecardTemplates;
    const defaultTimecard = this.timecardEntries?.defaultEmptyTimecard;
    const job = detailRow.original.job ?? defaultTimecard?.job;
    const group = job?.group ?? detailRow.original.group ?? defaultTimecard?.group;
    const occupation = job?.occupation ?? detailRow.original.occupation ?? defaultTimecard?.occupation;
    if (group && occupation) {
      const employee = detailRow.original.employee ?? defaultTimecard?.employee;
      const timecardsFromTemplates = timecardTemplates.map((tt: ITimecardTemplate) =>
        timecardTemplateToTimecard({
          timecardTemplate: tt,
          template: newTemplate,
          earnedDate: this.earnedDate,
          group,
          job,
          occupation,
          employee,
        }),
      );
      const timecards = this.timecardEntries.get();
      const newTimecards = timecards.filter(
        (timecard) =>
          !(
            (timecard.template?.id === existingTemplate?.id || timecard.template?.id === newTemplate.id) &&
            ['worked', 'break'].includes(timecard.reason?.category ?? '')
          ),
      );
      newTimecards.push(...timecardsFromTemplates);
      await this.load(
        newTimecards.find((t) => t.reason?.category === 'worked') ?? newTimecards[0],
        newTemplate,
        newTimecards,
        true,
      );
      this.emit('edit', this.currentRow);
    } else {
      notifications.error('group and occupation required for shift type template to generate');
    }
  };

  commit() {
    this.state.hasChanges = false;
  }

  get hasChanges(): boolean {
    return this.state.hasChanges;
  }

  canSave(): boolean {
    return !this.viewOnly && this.state.hasChanges;
  }

  getTimecards(): ITimecard[] | undefined {
    if (!this.canSave() || (this.state.__currentRow && !this.form.submit())) {
      return undefined;
    }
    return this.timecardEntries.get();
  }
}

export class EditShiftModel extends EditTimecardModel<{ viewMode: string }> {
  constructor(id: string, viewOnlyFn?: () => boolean) {
    super(id, {
      condensedTimecard: false,
      showPremiums: false,
      showUdfs: false,
      showBreaks: false,
      breakdownMode: false,
      timecardEntries: new ShiftEntries(EditShiftModel.jobModeGroupingKey),
      fieldMap: {
        group: { visible: false, columns: 2, udf: false, label: 'Group' },
        occupation: { visible: false, columns: 2, udf: false, label: 'Occupation' },
        template: { visible: false, columns: 2, udf: false, label: 'Shift Type' },
        start: { visible: true, columns: 1, udf: false, label: 'Start' },
        end: { visible: true, columns: 1, udf: false, label: 'End' },
        employee: { visible: true, columns: 2, udf: false, label: 'Employee' },
        units: { visible: true, columns: 1, udf: false, label: 'Units' },
        reason: { visible: true, columns: 2, udf: false, label: 'Reason' },
        overtimeReason: { visible: false, columns: 2, udf: false, label: 'Overtime Reason' },
        rate: { visible: false, columns: 1, udf: false, label: 'Rate' },
        factor: { visible: false, columns: 1, udf: false, label: 'Factor' },
        accountCode: { visible: false, columns: 2, udf: false, label: 'Account Code' },
        tags: { visible: false, columns: 2, udf: false, label: 'Tags' },
      },
      viewOnlyFn: viewOnlyFn,
    });
    this.viewMode = 'Job';
  }

  private static jobModeGroupingKey(timecard: TimecardEntry) {
    const fields = timecard.fields ? JSON.stringify(timecard.fields) : '';
    return `${timecard.isEmpty ? 0 : 1}${timecard?.reason?.id ?? ''}${timecard?.employee?.id ?? ''}${fields}`;
  }

  private static employeeModeGroupingKey(timecard: TimecardEntry) {
    const fields = timecard.fields ? JSON.stringify(timecard.fields) : '';
    return `${timecard.isEmpty ? 0 : 1}${timecard?.reason?.id ?? ''}${timecard?.occupation?.id ?? ''}${timecard?.template?.id ?? ''}${fields}`;
  }

  get viewMode() {
    return this.state.viewMode;
  }
  set viewMode(value: string) {
    if (this.state.viewMode === value) return;
    this.state.viewMode = value;
    if (value === 'Job') {
      this.timecardEntries = new ShiftEntries(EditShiftModel.jobModeGroupingKey);
      this._fieldMap.group!.visible = false;
      this._fieldMap.occupation!.visible = false;
      this._fieldMap.template!.visible = false;
      this._fieldMap.employee!.visible = true;
      this._standardTimecardFieldMapVisibility.group = false;
      this._standardTimecardFieldMapVisibility.occupation = false;
      this._standardTimecardFieldMapVisibility.template = false;
      this._standardTimecardFieldMapVisibility.employee = true;
    } else {
      this.timecardEntries = new EmployeeShiftEntries(EditShiftModel.employeeModeGroupingKey);
      this._fieldMap.group!.visible = true;
      this._fieldMap.occupation!.visible = true;
      this._fieldMap.template!.visible = true;
      this._fieldMap.employee!.visible = false;
      this._standardTimecardFieldMapVisibility.group = true;
      this._standardTimecardFieldMapVisibility.occupation = true;
      this._standardTimecardFieldMapVisibility.template! = true;
      this._standardTimecardFieldMapVisibility.employee! = false;
    }
    this.initializeTimecardEntries();
  }

  get autoCreateVacancies() {
    return this.timecardEntries.autoCreateVacancies;
  }
  set autoCreateVacancies(value: boolean) {
    this.timecardEntries.autoCreateVacancies = value;
  }
}
